import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AD_PLACEMENTS, type PlacementId } from "./adsConfig";
import { useAdConsent } from "./useAdConsent";
import { useAdFrequency } from "./useAdFrequency";
import { AdSenseUnit } from "./AdSenseUnit";
import { HouseAd } from "./HouseAd";
import { ADSENSE_ENABLED } from "@/lib/adsFlag";

interface Props {
  placement: PlacementId;
  /** Volitelný klíč pro rotaci house adů */
  seed?: string;
  className?: string;
}

/**
 * Universální reklamní slot. Sám řeší:
 * - frekvenční strop per page
 * - consent (před souhlasem = non-personalized AdSense + house fallback)
 * - lazy mount (viditelnost přes IntersectionObserver)
 * - layout shift (rezervovaná výška)
 */
export function AdSlot({ placement, seed, className }: Props) {
  const { t } = useTranslation();
  const config = AD_PLACEMENTS[placement];
  const consent = useAdConsent();
  const { allowed } = useAdFrequency(placement, config.maxPerPage);
  const containerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!allowed) return;
    const el = containerRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setVisible(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [allowed]);

  if (!allowed) return null;
  // While AdSense reviews the site, render nothing (no <ins> shells).
  if (!ADSENSE_ENABLED) return null;

  // Provider rozhodnutí
  const provider = config.provider as "adsense" | "house" | "auto";
  const useAdSense =
    provider === "adsense" ||
    (provider === "auto" && consent !== "denied");

  return (
    <div
      ref={containerRef}
      className={["w-full my-4", className].filter(Boolean).join(" ")}
      data-ad-placement={placement}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground/60 mb-1">
        {t("adConsent.label")}
      </div>
      <div style={{ minHeight: config.minHeight }}>
        {visible ? (
          useAdSense ? (
            <AdSenseUnit placement={config} />
          ) : (
            <HouseAd minHeight={config.minHeight} seed={seed ?? placement} />
          )
        ) : null}
      </div>
    </div>
  );
}
